import { useState, useEffect } from "react"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import usePaginationTable from "hooks/usePaginationTable"
import useFiltersTable from "hooks/useFiltersTable"
import { get_data_table_series, parsed_filters_on_search } from "helpers/data_tables"

export const useSeriesList = () => {
  const [refreshToken, setRefreshToken] = useState(0)
  const refreshSearch = () => setRefreshToken(t => t + 1)
  const [viewMode, setViewMode] = useState<"TABLE" | "BLOCK">("TABLE")
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  
  const [isRelatedOrdersModalOpen, setIsRelatedOrdersModalOpen] = useState(false)
  const [selectedSerieForOrders, setSelectedSerieForOrders] = useState<any>(null)
  
  // State for inline Estado Omnix edit
  const [isConfirmStateModalOpen, setIsConfirmStateModalOpen] = useState(false)
  const [pendingStateChange, setPendingStateChange] = useState<{ id: number, label: string } | null>(null)
  const [serieToUpdate, setSerieToUpdate] = useState<any>(null)

  // State for History
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const [serieForHistory, setSerieForHistory] = useState<any>(null)
  
  const [skuSearch, setSkuSearch] = useState("")
  const [serieSapSearch, setSerieSapSearch] = useState("")
  const [estadoOmnixSearch, setEstadoOmnixSearch] = useState<any[]>([])
  const [isAvailableSearch, setIsAvailableSearch] = useState<boolean | null>(null)
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null)

  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<any>(null)
  const [isStoresModalOpen, setIsStoresModalOpen] = useState(false)
  const [storesForModal, setStoresForModal] = useState<any[]>([])

  // Sorting state
  const [sortField, setSortField] = useState<string>("_omnixStateMachine.currentState.date")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortOrder("desc")
    }
  }

  const {
    loading_app,
    data,
    search_osrm_action,
  } = useSearchMiddlewareOSRM({
    entity: "series",
    silent404: true,
  })

  const {
    loading_app: loading_product,
    data: data_product,
    search_osrm_action: search_osrm_action_product,
  } = useSearchMiddlewareOSRM({
    entity: "items",
    silent404: true,
  })

  const { current_data, current_page, handle_page_change, handle_per_page, per_page, total_records, total_pages } =
    usePaginationTable({
      data,
      default_per_page: 10,
    })

  const { filters_table, apply_filter } = useFiltersTable({ 
    table_name: "series", 
    onFilterApplied: () => handle_page_change(0) 
  })

  const remove_filter = (id: string) => {
    filters_table.dispatch({ type: "DELETE_FILTER", payload: id })
    handle_page_change(0)
  }

  // Sync internal search states with filters_table
  useEffect(() => {
    const currentSkuFilter = filters_table.state.find((f: any) => f.id === "sku")?.value || ""
    setSkuSearch(currentSkuFilter)

    const currentSerieSapFilter = filters_table.state.find((f: any) => f.id === "serieSap")?.value || ""
    setSerieSapSearch(currentSerieSapFilter)

    const currentEstadoOmnixFilter = filters_table.state.find((f: any) => f.id === "estadoOmnix")?.value || []
    if (Array.isArray(currentEstadoOmnixFilter)) {
      const optionsMapInverse: Record<string, number> = {
        "0 - libre": 0,
        "1 - vendido": 1,
        "4 - reservado": 4,
        "6 - eliminado": 6,
        "7 - bloqueado": 7
      }
      const ids = currentEstadoOmnixFilter.map((val: any) => {
        if (typeof val === "number") return val
        return optionsMapInverse[val as string] ?? val
      })
      setEstadoOmnixSearch(ids)
    } else {
      setEstadoOmnixSearch([])
    }
    const currentIsAvailableFilter = filters_table.state.find((f: any) => f.id === "isAvailable")?.value
    setIsAvailableSearch(currentIsAvailableFilter === undefined ? null : currentIsAvailableFilter)
  }, [filters_table.state])

  // Fetch Series whenever pagination or filters change
  useEffect(() => {
    const rawFilters = parsed_filters_on_search(filters_table.state) as Record<string, any>
    const processedFilters: Record<string, any> = {}

    // Only include filters with non-empty values and handle comma-separated strings
    Object.keys(rawFilters).forEach(key => {
      let val = rawFilters[key]
      
      // Strict exclusion of empty/null values
      if (val === null || val === undefined) return
      if (typeof val === "string" && val.trim() === "") return
      if (Array.isArray(val) && val.length === 0) return
      
      // Handle comma-separated strings if they exist
      if (typeof val === "string" && val.includes(",")) {
        val = val.split(",").map((s: string) => s.trim()).filter(Boolean)
        if (val.length === 0) return
      }
      
      // Unwrap arrays of length 1 to simple strings as expected by the new OSRM pass-through
      if (Array.isArray(val) && val.length === 1) {
        val = val[0]
      }
      
      processedFilters[key] = val
    })

    search_osrm_action({
      from: per_page * current_page,
      size: per_page,
      filters: processedFilters,
      allProperties: true,
      sortBy: { [sortField]: sortOrder },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_page, per_page, filters_table.state, refreshToken, sortField, sortOrder])

  const [columns, data_table_items] = get_data_table_series(current_data)

  // SKU Preview logic
  const handlePreview = (item: any) => {
    setSelectedItem(item)
    setIsPreviewOpen(true)
  }

  const onRelatedOrdersPreview = (serie: any) => {
    setSelectedSerieForOrders(serie)
    setIsRelatedOrdersModalOpen(true)
  }

  const onRequestStateChange = (serie: any, newStateId: number, newStateLabel: string) => {
    setSerieToUpdate(serie)
    setPendingStateChange({ id: newStateId, label: newStateLabel })
    setIsConfirmStateModalOpen(true)
  }

  const onConfirmStateChangeSuccess = () => {
    setIsConfirmStateModalOpen(false)
    setSerieToUpdate(null)
    setPendingStateChange(null)
    setTimeout(() => {
      refreshSearch()
    }, 1000)
  }

  const onHistoryPreview = (serie: any) => {
    setSerieForHistory(serie)
    setIsHistoryModalOpen(true)
  }

  const handleProductPreview = (sku: string) => {
    setSelectedProduct(null)
    setIsProductModalOpen(true)
    search_osrm_action_product({
      from: 0,
      size: 1,
      filters: { sku: sku },
    })
  }

  useEffect(() => {
    if (data_product?.records?.length > 0) {
      setSelectedProduct(data_product.records[0])
    }
  }, [data_product])

  return {
    viewMode, setViewMode,
    isPreviewOpen, setIsPreviewOpen,
    selectedItem,
    // Orders Modal
    isRelatedOrdersModalOpen,
    setIsRelatedOrdersModalOpen,
    selectedSerieForOrders,
    setSelectedSerieForOrders,
    onRelatedOrdersPreview,
    
    // State Modal
    isConfirmStateModalOpen,
    setIsConfirmStateModalOpen,
    serieToUpdate,
    pendingStateChange,
    onRequestStateChange,
    onConfirmStateChangeSuccess,
    
    // History Modal
    isHistoryModalOpen,
    setIsHistoryModalOpen,
    serieForHistory,
    onHistoryPreview,
    
    skuSearch, setSkuSearch,
    serieSapSearch, setSerieSapSearch,
    estadoOmnixSearch, setEstadoOmnixSearch,
    isAvailableSearch, setIsAvailableSearch,
    openFilterColumn, setOpenFilterColumn,
    isProductModalOpen, setIsProductModalOpen,
    selectedProduct,
    isStoresModalOpen, setIsStoresModalOpen,
    storesForModal, setStoresForModal,
    loading_app, loading_product,
    data_product,
    current_data,
    current_page, handle_page_change,
    per_page, handle_per_page,
    total_records, total_pages,
    apply_filter,
    handlePreview,
    handleProductPreview,
    columns,
    data_table_items,
    filters_table,
    remove_filter,
    search_error: data?._isError ? data : null,
    refreshSearch,
    sortField,
    sortOrder,
    handleSort,
  }
}
